// ========================================
// pages/Pricing.js - Plan Pricing & Comparison
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Display all plans with one-time pricing and feature comparison
// 
// PRICING MODEL: ONE-TIME PAYMENT (Lifetime Access)
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  const plans = [
    {
      name: 'Free',
      price: 'GHS 0',
      period: 'one-time',
      icon: '/images/plans/plan-free.png',
      badge: null,
      features: [
        '2 Global Next of Kin',
        'Up to 10 Assets',
        '2 Trustees',
        'No External Beneficiaries',
        '2MB file uploads',
        'Basic verification'
      ],
      buttonText: 'Get Started',
      buttonLink: '/register',
      popular: false
    },
    {
      name: 'Premium',
      price: 'GHS 399',
      period: 'one-time',
      icon: '/images/plans/plan-premium.png',
      badge: 'MOST POPULAR',
      features: [
        '4 Global Next of Kin',
        'Up to 50 Assets',
        'Up to 3 Trustees',
        '1 External Beneficiary PER ASSET',
        '5MB file uploads',
        'Choose 1 or 2 of 3 Verifiers'
      ],
      buttonText: 'Choose Premium',
      buttonLink: '/register',
      popular: true
    },
    {
      name: 'Business',
      price: 'GHS 999',
      period: 'one-time',
      icon: '/images/plans/plan-business.png',
      badge: null,
      features: [
        '6 Global Next of Kin',
        'Up to 100 Assets',
        'Up to 6 Trustees',
        '2 External Beneficiaries PER ASSET',
        '10MB file uploads',
        'Choose 1, 2, or 3 of 6 Verifiers',
        'Priority email support'
      ],
      buttonText: 'Choose Business',
      buttonLink: '/register',
      popular: false
    },
    {
      name: 'Enterprise',
      price: 'GHS 2,999',
      period: 'one-time',
      icon: '/images/plans/plan-enterprise.png',
      badge: 'BEST VALUE',
      features: [
        '10 Global Next of Kin',
        'Up to 400 Assets',
        'Up to 10 Trustees',
        '3 External Beneficiaries PER ASSET',
        '20MB file uploads',
        'Choose 1-4 of 10 Verifiers',
        'Priority 24/7 support',
        'Custom integration options'
      ],
      buttonText: 'Contact Sales',
      buttonLink: '/contact',
      popular: false
    }
  ];

  return (
    <div className="pricing-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO */}
      {/* ======================================== */}
      <section className="pricing-hero">
        <div className="pricing-hero-overlay"></div>
        <div className="pricing-container">
          <div className="pricing-hero-content">
            <div className="pricing-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="pricing-badge-icon" />
              <span>Simple, Transparent Pricing</span>
            </div>
            <h1 className="pricing-hero-title">
              Choose the <span className="gold">Plan</span> That's Right for You
            </h1>
            <p className="pricing-hero-subtitle">
              One-time payment • Lifetime access • No recurring fees
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: PLAN CARDS */}
      {/* ======================================== */}
      <section className="pricing-plans">
        <div className="pricing-container">
          <div className="pricing-grid">
            {plans.map((plan, idx) => (
              <div key={idx} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.badge && <div className="pricing-badge-tag">{plan.badge}</div>}
                <img src={plan.icon} alt={plan.name} className="pricing-plan-icon" />
                <h3 className="pricing-plan-name">{plan.name}</h3>
                <div className="pricing-price">
                  <span className="pricing-amount">{plan.price}</span>
                  <span className="pricing-period">{plan.period}</span>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      <span className="pricing-check">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to={plan.buttonLink} className={`pricing-btn ${plan.popular ? 'gold' : ''}`}>
                  {plan.buttonText}
                </Link>
              </div>
            ))}
          </div>
          
          <div className="pricing-note">
            <p>💡 All plans are <strong>ONE-TIME PAYMENT</strong> - pay once, use forever. No recurring subscriptions or hidden fees.</p>
            <p className="pricing-pin-note">📌 Have a voucher PIN? <Link to="/upgrade">Upgrade here →</Link></p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: FAQ LINK */}
      {/* ======================================== */}
      <section className="pricing-faq">
        <div className="pricing-container">
          <div className="pricing-faq-card">
            <h3>Have questions about our plans?</h3>
            <p>Check our FAQ for answers to common questions about pricing, upgrades, and features.</p>
            <Link to="/faq" className="pricing-faq-btn">View FAQ →</Link>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 4: CALL TO ACTION */}
      {/* ======================================== */}
      <section className="pricing-cta">
        <div className="pricing-container">
          <div className="pricing-cta-content">
            <h2 className="pricing-cta-title">Ready to Secure Your Family's Future?</h2>
            <p className="pricing-cta-subtitle">
              Start with our Free plan - no credit card required. Upgrade anytime.
            </p>
            <div className="pricing-cta-buttons">
              <Link to="/register" className="pricing-cta-primary">Create Free Account</Link>
              <Link to="/how-it-works" className="pricing-cta-secondary">Learn How It Works</Link>
            </div>
          </div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{ __html: `
        .pricing-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .pricing-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold { color: #FFD700; }
        
        /* Hero */
        .pricing-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .pricing-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .pricing-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .pricing-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .pricing-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .pricing-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .pricing-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .pricing-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Plan Cards */
        .pricing-plans {
          padding: 80px 0;
          background: white;
        }
        
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }
        
        .pricing-card {
          background: white;
          border-radius: 15px;
          padding: 30px 20px;
          text-align: center;
          position: relative;
          box-shadow: 0 5px 20px rgba(0,0,0,0.08);
          transition: transform 0.3s, box-shadow 0.3s;
          border: 1px solid #eee;
        }
        
        .pricing-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.1);
        }
        
        .pricing-card.popular {
          border: 2px solid #FFD700;
          transform: scale(1.02);
        }
        
        .pricing-badge-tag {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: #FFD700;
          color: #0A0A0A;
          padding: 5px 15px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: bold;
          white-space: nowrap;
        }
        
        .pricing-plan-icon {
          height: 70px;
          width: auto;
          margin-bottom: 20px;
        }
        
        .pricing-plan-name {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 15px;
          color: #1A1A1A;
        }
        
        .pricing-price {
          margin-bottom: 25px;
        }
        
        .pricing-amount {
          font-size: 32px;
          font-weight: bold;
          color: #FFD700;
        }
        
        .pricing-period {
          font-size: 12px;
          color: #666;
          display: block;
        }
        
        .pricing-features {
          list-style: none;
          padding: 0;
          margin-bottom: 30px;
          text-align: left;
        }
        
        .pricing-features li {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          font-size: 13px;
          color: #444;
        }
        
        .pricing-check {
          color: #FFD700;
          font-weight: bold;
        }
        
        .pricing-btn {
          display: block;
          width: 100%;
          padding: 12px;
          background: #1A1A1A;
          color: white;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
          text-align: center;
        }
        
        .pricing-btn.gold {
          background: #FFD700;
          color: #1A1A1A;
        }
        
        .pricing-btn:hover {
          background: #333;
        }
        
        .pricing-btn.gold:hover {
          background: #B8860B;
        }
        
        .pricing-note {
          text-align: center;
          margin-top: 50px;
          font-size: 14px;
          color: #666;
        }
        
        .pricing-pin-note {
          margin-top: 10px;
        }
        
        .pricing-pin-note a {
          color: #FFD700;
          text-decoration: none;
        }
        
        /* FAQ Card */
        .pricing-faq {
          padding: 0 0 80px 0;
        }
        
        .pricing-faq-card {
          background: #F5F0E6;
          border-radius: 15px;
          padding: 40px;
          text-align: center;
        }
        
        .pricing-faq-card h3 {
          font-size: 24px;
          margin-bottom: 15px;
        }
        
        .pricing-faq-card p {
          color: #666;
          margin-bottom: 20px;
        }
        
        .pricing-faq-btn {
          display: inline-block;
          padding: 12px 30px;
          background: #FFD700;
          color: #1A1A1A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .pricing-faq-btn:hover {
          background: #B8860B;
        }
        
        /* CTA */
        .pricing-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          text-align: center;
        }
        
        .pricing-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .pricing-cta-title {
          font-size: 32px;
          color: white;
          margin-bottom: 20px;
        }
        
        .pricing-cta-subtitle {
          font-size: 18px;
          color: #ccc;
          margin-bottom: 30px;
        }
        
        .pricing-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .pricing-cta-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .pricing-cta-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        @media (max-width: 1024px) {
          .pricing-grid { grid-template-columns: repeat(2, 1fr); }
          .pricing-hero-title { font-size: 40px; }
        }
        
        @media (max-width: 768px) {
          .pricing-grid { grid-template-columns: 1fr; }
          .pricing-hero-title { font-size: 28px; }
          .pricing-card.popular { transform: scale(1); }
          .pricing-cta-title { font-size: 24px; }
        }
      `}} />
    </div>
  );
};

export default Pricing;